// src/pages/DistrictPage.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { useBrand } from '../brand/BrandContext';
import { detectParentInfo } from '../utils/parentOrigin';
import { VideoPopupProvider, useVideoPopup } from '../components/VideoPopupContext';

// Import the Legacy Page Component
import LegacyDistrictPage from './LegacyDistrictPage';

// Icons for the Enterprise Dashboard
import {
  Search, Video, Settings, HelpCircle, LogOut,
  List, Clock, Play, Volume2, Maximize, BarChart2,
  TrendingUp, MessagesSquare, PieChart, FileCheck
} from 'lucide-react';

// Auth / subscription (shared contract from src/auth)
import { useAuth } from '../auth/AuthContext';
import { useSubscription } from '../auth/useSubscription';
import AccessGate from '../auth/AccessGate';

// Shared Components
import Meetings from '../components/Meetings';
import Topics from '../components/Topics';
import VotingAnalysis from '../components/VotingAnalysis';
import ForecastsTab from '../components/ForecastsTab';
import AnalysisTab from '../components/AnalysisTab';
import BillsPassed from '../components/BillsPassed';
import CommunityVoice from '../components/CommunityVoice';
import SearchBar from '../components/SearchBar';
import SearchResults from './SearchResults';
import Chatbot from '../components/Chatbot';
import WidgetAccessDenied from '../components/WidgetAccessDenied';
import VimeoPlayerEmbed from '../components/VimeoPlayerEmbed';

import './DistrictPage.css'; // Contains Enterprise Styles

/* =========================================================================
   HELPER: SEARCH TITLE GENERATOR
   ========================================================================= */
const generateSmartTitle = (chunk, searchTerm) => {
  if (chunk.agenda_item) return chunk.agenda_item;
  const text = chunk.content || chunk.text || '';
  const timeMatch = text.match(/\[(\d{1,2}:\d{2}(?::\d{2})?)\]/);
  if (timeMatch) return `Transcript at ${timeMatch[1]}`;
  return `Mention of "${searchTerm}"`;
};

/* =========================================================================
   COMPONENT: ENTERPRISE DASHBOARD (PAUSD ONLY)
   ========================================================================= */

function EnterpriseDashboardInner({ isWidget = false }) {
  const { abbreviation } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const boardRef = useRef(null);
  const containerRef = useRef(null);
  
  // Consume Context for Right Sidebar Video
  const { videoState, closeVideo, showPopup, loadVideo } = useVideoPopup();

  /* ───────── State ───────── */
  const [activeTab, setActiveTab] = useState('Meetings');
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-tenant config derived from the fetched district row (replaces the
  // former hardcoded pausd/suhsd checks). Falsy until the district loads.
  const showTopics = !!district?.topics_tab_enabled;
  // Forecasts / Community Voice are dashboard-only features — never shown in
  // the embed widget, and gated on their per-tenant config columns.
  // "Forecasts" is the user-facing name for every tenant; the DB config column
  // is still predictions_enabled. The word "Predictions" was once reserved for
  // a separate feature — that feature shipped as "Prediction" and has since
  // been renamed to "Analysis" (see showAnalysis below); the bare word is no
  // longer reserved for anything.
  const showForecasts = !!district?.predictions_enabled && !isWidget;
  // Analysis (attention-by-category analytics; renamed from "Prediction") is
  // city-tenant only for now: its category taxonomy is city quality-of-life
  // oriented. Config-driven via entity_type, per the tenant-config rule.
  const showAnalysis = district?.entity_type === 'city_council' && !isWidget;
  // Bills Passed shares the Analysis taxonomy, so it carries the same
  // city-only gate.
  const showBillsPassed = district?.entity_type === 'city_council' && !isWidget;
  const showCommunityVoice = !!district?.community_voice_enabled && !isWidget;
  const useProxy = !!(district?.is_public || district?.is_first_party);

  // Real auth/subscription state for the sidebar user block. The `|| {}`
  // guards keep destructuring safe if a provider ever returns nothing.
  const { user, openLogin, signOut } = useAuth() || {};
  const { status: subscriptionStatus, isAdmin: userIsAdmin } = useSubscription() || {};
  // Same gate the navbar uses: hide the sign-in affordance on tenants where
  // accounts don't unlock anything (dashboard_gate/premium_gate both 'none').
  const { showAccountMenu } = useBrand();

  // Search State
  const [results, setResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLimit, setSearchLimit] = useState(15);
  // Tracks the latest in-flight search so older racing responses can't clobber it.
  const searchRequestIdRef = useRef(0);
  // AbortController for the current in-flight fetch.
  const searchAbortRef = useRef(null);
  // Term that's currently displayed in the SearchBar input — used to drop
  // stale results the moment the user types something different.
  const lastTypedTermRef = useRef('');

  // Right Sidebar State (Active Meeting Agenda)
  const [activeAgendaItems, setActiveAgendaItems] = useState([]);
  
  // Resizable Sidebar State
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [isResizing, setIsResizing] = useState(false);

  // Widget Security
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Chatbot State
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Browser-derived parent info (ancestorOrigins / document.referrer).
  // Cannot be spoofed by a fake ?parentUrl query param.
  const parentInfo = useMemo(() => detectParentInfo(), []);

  /* ───────── Seamless Resize Logic (Widget) ───────── */
  useEffect(() => {
    if (!isWidget) return;
    const sendHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({ type: 'resize', height }, '*');
      }
    };
    const observer = new ResizeObserver(sendHeight);
    if (containerRef.current) observer.observe(containerRef.current);
    sendHeight();
    return () => observer.disconnect();
  }, [isWidget, activeTab, results, loading, searchLoading, isChatOpen, videoState, activeAgendaItems, sidebarWidth]);

  /* ───────── Sidebar Resizing Logic ───────── */
  const startResizing = useCallback((e) => { e.preventDefault(); setIsResizing(true); }, []);
  const stopResizing = useCallback(() => { setIsResizing(false); }, []);
  const resize = useCallback((e) => {
      if (isResizing) {
          const newWidth = window.innerWidth - e.clientX;
          if (newWidth > 200 && newWidth < 800) setSidebarWidth(newWidth);
      }
  }, [isResizing]);

  useEffect(() => {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      return () => {
          window.removeEventListener("mousemove", resize);
          window.removeEventListener("mouseup", stopResizing);
      };
  }, [resize, stopResizing]);

  /* ───────── Sync Tab & Scroll ───────── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab')?.toLowerCase();
    const hash = location.hash;

    let currentTab = 'Meetings';
    switch (tabParam) {
      case 'search': currentTab = 'Search'; break;
      case 'voting':
      case 'votinganalysis': currentTab = 'Voting'; break;
      case 'billspassed':
      case 'bills-passed': currentTab = showBillsPassed ? 'BillsPassed' : 'Meetings'; break;
      case 'topics': currentTab = showTopics ? 'Topics' : 'Meetings'; break;
      case 'forecasts': currentTab = showForecasts ? 'Forecasts' : 'Meetings'; break;
      // 'analysis' is the current slug. 'prediction'/'predictions' are kept as
      // aliases for links shared before this tab was renamed from "Prediction".
      case 'analysis':
      case 'prediction':
      case 'predictions': currentTab = showAnalysis ? 'Analysis' : 'Meetings'; break;
      case 'communityvoice':
      case 'community': currentTab = showCommunityVoice ? 'CommunityVoice' : 'Meetings'; break;
      default: currentTab = 'Meetings';
    }
    setActiveTab(currentTab);

    if (hash && hash.startsWith('#meeting-')) {
      setActiveTab('Meetings');
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [location, showTopics, showForecasts, showAnalysis, showBillsPassed, showCommunityVoice]);

  /* ───────── Fetch district meta ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      if (isWidget) {
        try {
          const res = await fetch('/.netlify/functions/districtData', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resource: 'district',
              abbreviation,
              parentOrigin: parentInfo.origin,
              parentUrl: parentInfo.url,
            }),
          });

          if (res.status === 403) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload.error || 'Failed to load district');
          }
          const payload = await res.json();
          setDistrict(payload.district);
          setLoading(false);
          return;
        } catch (err) {
          setError(err);
          setLoading(false);
          return;
        }
      }

      // Supabase direct fetch (for non-widget mode)
      const { data, error } = await supabase
        .from('Districts')
        .select('*')
        .eq('abbreviation', abbreviation)
        .single();

      if (error) setError(error);
      else setDistrict(data);

      setLoading(false);
    })();
  }, [abbreviation, isWidget, parentInfo]);

  const chooseTab = (tabID) => {
    if (['Settings', 'Help'].includes(tabID)) return;
    setActiveTab(tabID);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabID.toLowerCase());
    navigate(`?${params.toString()}`);
    if (window.innerWidth < 768 && boardRef.current) {
      boardRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /* ───────── SEARCH LOGIC (Updated with Date Filtering) ───────── */
  const executeSearch = useCallback(
    async (term, scope = 'all', limit = 15, startDate = null, endDate = null, loose = false) => {
      if (!term.trim()) return;
      if (!district?.id) return;

      // Abort any prior in-flight fetch and mark this as the latest request.
      if (searchAbortRef.current) {
        try { searchAbortRef.current.abort(); } catch (e) {}
      }
      const controller = new AbortController();
      searchAbortRef.current = controller;
      const myRequestId = ++searchRequestIdRef.current;
      if (limit === 15) setSearchLoading(true);

      try {
        const response = await fetch('/.netlify/functions/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            term,
            scope,
            maxResults: limit,
            districtId: district.id,
            abbreviation,
            parentOrigin: parentInfo.origin,
            parentUrl: parentInfo.url,
            startDate: startDate || null,
            endDate: endDate || null,
            loose: Boolean(loose)
          }),
          signal: controller.signal,
        });

        if (myRequestId !== searchRequestIdRef.current) return; // stale
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        if (myRequestId !== searchRequestIdRef.current) return; // stale

        const isArrayResponse = Array.isArray(data);
        const meetingItemsRaw = isArrayResponse ? data : data.meeting_items || [];
        const transcriptRaw = isArrayResponse ? [] : data.transcript_matches || [];
        const looseAvailable = !isArrayResponse && data.loose_available === true;

        const meetingResults = (meetingItemsRaw || []).map((item) => ({
          ...item, type: item.type || 'discussion', content: item.content || item.summary || '',
        }));

        let transcriptResults = [];
        if (transcriptRaw.length > 0) {
          const meetingIds = [...new Set(transcriptRaw.map((t) => t.meeting_id).filter(Boolean))];
          let videoMap = {};
          if (meetingIds.length > 0) {
            const { data: videoData } = await supabase.from('Meetings').select('id, youtube_link').in('id', meetingIds);
            if (videoData) videoData.forEach((row) => { videoMap[row.id] = row.youtube_link; });
          }
          if (myRequestId !== searchRequestIdRef.current) return; // stale
          transcriptResults = transcriptRaw.map((chunk) => ({
            ...chunk,
            title: generateSmartTitle(chunk, term),
            type: 'discussion',
            content: chunk.content || chunk.text || '',
            date: chunk.date,
            video_url: videoMap[chunk.meeting_id] || null,
          }));
        }

        setResults({
            summaries: [...meetingResults, ...transcriptResults],
            topics: [],
            term,
            scope,
            startDate,
            endDate,
            loose: Boolean(loose),
            looseAvailable: looseAvailable
        });
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.error('Search failed:', err);
        if (myRequestId === searchRequestIdRef.current) {
          // Real failure on the latest search — clear results so the user
          // doesn't see a stale "no results for old term" message.
          setResults(null);
        }
      } finally {
        if (myRequestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    },
    [abbreviation, district, parentInfo]
  );

  // Fired on every keystroke in the SearchBar (not debounced). Used to drop
  // stale results the moment the user is typing a different term, so a
  // previous "no results for X" message can't sit there until the new
  // debounced search finishes.
  const handleSearchTermChange = useCallback((nextTerm) => {
    const trimmed = (nextTerm || '').trim();
    const prev = lastTypedTermRef.current;
    lastTypedTermRef.current = trimmed;
    if (trimmed === prev) return;

    // Invalidate any in-flight search.
    ++searchRequestIdRef.current;
    if (searchAbortRef.current) {
      try { searchAbortRef.current.abort(); } catch (e) {}
      searchAbortRef.current = null;
    }
    setResults(null);
    // Keep the loading skeleton visible from the first keystroke through the
    // debounce + backend fetch, so the user never sees a "no results" or
    // "begin your search" flash while they're still composing or waiting.
    setSearchLoading(Boolean(trimmed));
  }, []);

  const handleSearch = useCallback((term, scope = 'all', startDate = null, endDate = null) => {
      setSearchLimit(15);
      setResults(null);
      
      if(term.trim()) {
        const params = new URLSearchParams(location.search);
        params.set('tab', 'search');
        navigate(`?${params.toString()}`, { replace: true });
        executeSearch(term, scope, 15, startDate, endDate);
      }
  }, [executeSearch, navigate, location.search]);

  // Single Load More handler: bumps the limit and, if the tight result set
  // has been exhausted but the backend says more chunks exist below the
  // relevance floor, automatically promotes the search to loose mode so the
  // next page picks them up. Once loose, we stay loose.
  const handleLoadMore = () => {
    if (!results?.term) return;
    const newLimit = searchLimit + 15;
    setSearchLimit(newLimit);

    const isCurrentlyLoose = Boolean(results.loose);
    const exhaustedTight = (results.summaries?.length || 0) < searchLimit;
    const useLoose = isCurrentlyLoose || (exhaustedTight && results.looseAvailable === true);

    executeSearch(results.term, results.scope || 'all', newLimit, results.startDate, results.endDate, useLoose);
  };

  const handleMeetingActive = useCallback((agendaItems, youtubeLink) => {
      const enriched = (agendaItems || []).map(item => ({...item, youtubeLink}));
      setActiveAgendaItems(enriched);
      if (youtubeLink && loadVideo) loadVideo(youtubeLink);
  }, [loadVideo]);

  const handleCloseVideo = () => {
      closeVideo();
      setActiveAgendaItems([]);
  };

  /* ───────── Render Content ───────── */
  // Core tabs honor the tenant's dashboard_gate ('none'|'account'|'subscription').
  // Widget/embed mode is never gated (it goes through the service-role proxy).
  const dashboardGate = isWidget ? 'none' : (district?.dashboard_gate || 'none');
  const gateWrap = (node) => (
    <AccessGate gate={dashboardGate} featureName="This dashboard">
      {node}
    </AccessGate>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'Search':
        return gateWrap(
          <div className="search-tab-container">
            <div className="search-card">
              <div className="search-card-header">
                <h3>Search Board Intelligence</h3>
                <p>Search across meeting summaries and raw transcripts.</p>
                <SearchBar onSearch={handleSearch} onTermChange={handleSearchTermChange} isSearching={searchLoading} />
              </div>
              <div className="search-card-results">
                <SearchResults
                  results={results}
                  isLoading={searchLoading}
                  onLoadMore={handleLoadMore}
                  hasMore={Boolean(
                    (results?.summaries?.length || 0) >= searchLimit
                    || (!results?.loose && results?.looseAvailable === true)
                  )}
                  onClearSearch={() => { setResults(null); }}
                  isWidget={isWidget}
                  parentInfo={parentInfo}
                  useProxy={useProxy}
                />
              </div>
            </div>
          </div>
        );
      case 'Meetings':
        return gateWrap(<Meetings isWidget={isWidget} parentInfo={parentInfo} onMeetingActive={handleMeetingActive} isLegacy={false} />);
      case 'Topics':
         return showTopics ? gateWrap(<Topics isWidget={isWidget} parentInfo={parentInfo} />) : null;
      case 'Voting':
        return gateWrap(<VotingAnalysis />);
      case 'Forecasts':
        return showForecasts ? <ForecastsTab /> : null;
      case 'Analysis':
        return showAnalysis ? <AnalysisTab /> : null;
      case 'BillsPassed':
        return showBillsPassed ? <BillsPassed /> : null;
      case 'CommunityVoice':
        return showCommunityVoice ? <CommunityVoice /> : null;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="page-loading-container">
        <div className="loading-spinner"></div>
        <p>Loading district info...</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div ref={containerRef}>
        <WidgetAccessDenied parentInfo={parentInfo} />
      </div>
    );
  }

  if (error) return <p>Error: {error.message}</p>;

  const navItems = [
    { id: 'Search', label: 'Search', icon: Search },
    { id: 'Meetings', label: 'Meetings', icon: Video },
    // Topics tab is gated on the tenant's topics_tab_enabled config (matches the
    // renderTab gate). Without this entry the enterprise dashboard had no way to
    // reach the Topics tab even when enabled.
    ...(showTopics ? [{ id: 'Topics', label: 'Topics', icon: List }] : []),
    { id: 'Voting', label: 'Voting', icon: BarChart2 },
    ...(showBillsPassed ? [{ id: 'BillsPassed', label: 'Bills Passed', icon: FileCheck }] : []),
    // Forecasts / Community Voice are config-gated per tenant (and never
    // shown in widget mode — the show flags already include !isWidget).
    ...(showForecasts ? [{ id: 'Forecasts', label: 'Forecasts', icon: TrendingUp }] : []),
    ...(showAnalysis ? [{ id: 'Analysis', label: 'Analysis', icon: PieChart }] : []),
    ...(showCommunityVoice ? [{ id: 'CommunityVoice', label: 'Community Voice', icon: MessagesSquare }] : []),
    { id: 'Settings', label: 'Settings', icon: Settings },
    { id: 'Help', label: 'Help & Support', icon: HelpCircle }
  ];

  // Sidebar user block: derived display values from the real auth session.
  const userEmail = user?.email || '';
  const userDisplayName = userEmail ? userEmail.split('@')[0] : '';
  const avatarLetter = userEmail ? userEmail.charAt(0).toUpperCase() : '?';
  const subscriptionLabel = userIsAdmin
    ? 'Admin'
    : subscriptionStatus === 'trialing'
      ? 'Free trial'
      : subscriptionStatus === 'active' || subscriptionStatus === 'past_due'
        ? 'Subscriber'
        : 'Free account';

  /* NOTE: Using 'ent-district-page' and 'ent-content-area' to avoid CSS conflicts with Legacy page */
  return (
    <div className={`ent-district-page ${isWidget ? 'widget-mode' : ''} ${isResizing ? 'resizing' : ''}`} ref={containerRef}>
      
      <div className="district-content-grid" style={{ gridTemplateColumns: `240px 1fr ${sidebarWidth}px` }}>
        
      <aside className="district-sidebar-left">
        <div className="sidebar-menu-list" ref={boardRef}>
            {navItems.map((item) => {
                const isActive = activeTab === item.id;
                const Icon = item.icon;
                return (
                    <div
                        key={item.id}
                        onClick={() => chooseTab(item.id)}
                        className={`sidebar-menu-item ${isActive ? 'active' : ''}`}
                    >
                        <Icon size={20} />
                        <span className="menu-label">{item.label}</span>
                    </div>
                );
            })}
        </div>

        {!isWidget && (showAccountMenu || user) && (
          <div className="sidebar-user-section">
              <div className="user-profile">
                  <div className="avatar">{avatarLetter}</div>
                  <div className="user-info">
                      {user ? (
                        <>
                          <span className="user-name" title={userEmail}>{userDisplayName}</span>
                          <span className="user-role">{subscriptionLabel}</span>
                        </>
                      ) : (
                        <>
                          <span className="user-name">Not signed in</span>
                          <button
                            type="button"
                            className="user-role user-signin-link"
                            onClick={() => { if (openLogin) openLogin(); }}
                          >
                            Sign in
                          </button>
                        </>
                      )}
                  </div>
                  {user && <div className="status-dot"></div>}
              </div>
              {user && (
                <div className="logout-button" onClick={() => { if (signOut) signOut(); }}>
                    <LogOut size={18} />
                    <span>Logout</span>
                </div>
              )}
          </div>
        )}
      </aside>

        <main className="ent-content-area">
            {renderTab()}
            <p className="page-copyright">
              © {new Date().getFullYear()} BoardWatch. All rights reserved.
            </p>
        </main>

        <div className="resizer-handle" onMouseDown={startResizing}></div>

        <aside className="district-sidebar-right">
             {videoState ? (
               <div className="sticky-video-player">
                  <div className="video-header">
                     <span className="video-label">Active Clip</span>
                     <div className="video-header-actions">
                        {videoState.externalUrl && (
                          <a
                            className="video-external-link"
                            href={videoState.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={
                              videoState.platform === 'vimeo'
                                ? "Open on Vimeo (use this if the embed says the video can't be played here)"
                                : 'Open in a new tab'
                            }
                          >
                            Open ↗
                          </a>
                        )}
                        <button className="close-video-btn" onClick={handleCloseVideo}>×</button>
                     </div>
                  </div>
                  <div className="video-frame-container">
                     {videoState.platform === 'vimeo' ? (
                        <VimeoPlayerEmbed
                           src={videoState.src}
                           title="Board Meeting Video"
                           externalUrl={videoState.externalUrl}
                        />
                     ) : (
                        <iframe
                           key={videoState.src}
                           src={videoState.src}
                           title="Board Meeting Video"
                           frameBorder="0"
                           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                           allowFullScreen
                        />
                     )}
                  </div>
               </div>
            ) : (
                <div className="empty-state-container">
                    <div className="mock-video-player">
                        <div className="mock-video-screen">
                            <div className="mock-play-button-wrapper">
                                <Play fill="currentColor" size={24} />
                            </div>
                        </div>
                        <div className="mock-video-controls">
                            <div className="mock-control-bar">
                                <div className="mock-progress" style={{width: '35%'}}></div>
                            </div>
                            <div className="mock-control-icons">
                                <div className="mock-icon-group">
                                    <Play size={14} />
                                    <Volume2 size={14} />
                                    <Maximize size={14} />
                                </div>
                                <div className="mock-time">04:32 / 12:45</div>
                            </div>
                        </div>
                    </div>
                    
                    <h3 className="empty-state-title">Ready to Review</h3>
                    <p className="empty-state-desc">
                        Select a meeting from the list to load the video recording and interactive agenda.
                    </p>

                    <div className="empty-state-features">
                        <div className="feature-item">
                            <div className="feature-icon-box"><Video size={16} /></div>
                            <span>HD Video Playback</span>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon-box"><List size={16} /></div>
                            <span>Smart Agenda Indexing</span>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon-box"><Clock size={16} /></div>
                            <span>Timestamp Navigation</span>
                        </div>
                    </div>
                </div>
            )}

            {activeAgendaItems.length > 0 && (
                <div className="sidebar-agenda-list">
                    <h4 className="sidebar-label">Agenda & Timestamps</h4>
                    {activeAgendaItems.map((item, idx) => (
                        <div
                            key={idx}
                            className="sidebar-agenda-item"
                            onClick={() => {
                                if (item.timestamp_start && item.youtubeLink) {
                                    showPopup(item.timestamp_start, item.youtubeLink);
                                }
                            }}
                        >
                            <span className="agenda-time">{item.timestamp_start || '00:00'}</span>
                            <span className="agenda-title">{item.title}</span>
                        </div>
                    ))}
                </div>
            )}
        </aside>

      </div>

      {/* ─────────────────────────────────────────────────────────────
          CHATBOT LAUNCHER (CONDITIONALLY RENDERED BASED ON FEATURE FLAG)
         ───────────────────────────────────────────────────────────── */}
      {district?.id && district?.premium_chat_enabled && (
          <div className={`chat-widget-container ${isChatOpen ? 'open' : ''}`}>
              {isChatOpen && (
                  <Chatbot
                      districtId={district.id}
                      onClose={() => setIsChatOpen(false)}
                  />
              )}
              <button
                  className="chat-launcher"
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  aria-label="Toggle Chat"
              >
                  {isChatOpen ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="launcher-icon">
                          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                  ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="launcher-icon">
                          <path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4-7.2-6-4.8h7.6z" />
                      </svg>
                  )}
              </button>
          </div>
      )}
    </div>
  );
}

function EnterpriseDistrictPage(props) {
    return (
        <VideoPopupProvider>
            <EnterpriseDashboardInner {...props} />
        </VideoPopupProvider>
    );
}

/* =========================================================================
   MAIN COMPONENT: SWITCHER
   ========================================================================= */
export default function DistrictPage(props) {
  const { abbreviation } = useParams();
  const { setBrandFromDistrict } = useBrand();
  // Layout is now a per-tenant config column, not a hardcoded abbreviation.
  // null = still resolving. `Districts` is blanket anon-readable and layout/
  // entity_type/brand_slug are non-sensitive UI config, so this lightweight read
  // works in both widget and non-widget modes without touching the origin-gated
  // content path. The child components keep their own full-district fetch.
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('Districts')
        .select('layout, entity_type, brand_slug, dashboard_gate, premium_gate')
        .eq('abbreviation', abbreviation)
        .single();
      if (!alive) return;
      if (data) {
        setBrandFromDistrict(data);
        setLayout(data.layout === 'enterprise' ? 'enterprise' : 'legacy');
      } else {
        setLayout('legacy'); // unknown district -> legacy (matches old default)
      }
    })();
    return () => { alive = false; };
  }, [abbreviation, setBrandFromDistrict]);

  if (layout === null) return null; // brief; each layout shows its own loader next
  if (layout === 'enterprise') {
    return <EnterpriseDistrictPage {...props} />;
  }
  return <LegacyDistrictPage {...props} />;
}